import {useRef} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";

import {useOrganizations} from '../hooks/useOrganizations.ts';
import {useOrganizationsFilter} from '../hooks/useOrganizationsFilter.ts';
import {useOrganizationsSelection} from '../hooks/useOrganizationsSelection.ts';
import {useOrganizationsSearch} from "../hooks/useOrganizationsSearch.ts";
import {useOrganizationsTableColumns} from "../hooks/useOrganizationsTableColumns.tsx";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {Organization} from '../types.ts';

export const RecordsTable = () => {

    const {allowedActions} = useVerifiedPermissions()

    const navigate = useNavigate();

    const {filters} = useOrganizationsFilter();

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useOrganizations(filters);

    const {
        columns,
    } = useOrganizationsTableColumns();

    // Same module-scoped store the list hook filters on — this component owns
    // the input, useOrganizations owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useOrganizationsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection
    } = useOrganizationsSelection();

    // Checkboxes appear only for an operator who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: Organization[] = [];
    if (allowedActions.includes(rbacActions.DeleteOrganization)) {
        selectedRecords = records.filter(record => selection[record.id] === true)
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that they have no organizations at all.
    const noRecordsText = keyword
        ? `No organizations match "${keyword}" in the records loaded so far.`
        : 'No organizations yet.';

    return (
        <Stack>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load organizations'>
                    The organizations API did not return the list. Try Reload; if it persists
                    the API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach((item: any) => {
                        handleRowSelection(item.id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                onRowClick={({record}) =>
                    navigate(`/organizations/${encodeURIComponent((record as Organization).id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='organization'
            />
        </Stack>
    );
};
